import { apiFetch } from "../../utils/apiFetch";

/**
 * Stammdaten eines gewählten Kontakts für die Beteiligten der Immobilie:
 * Anschrift (Eigentümer), Bankverbindungen (Hauskonto) und Mitarbeiter
 * (Ansprechpartner der Verwaltung). Fachabstimmung 31.08.2026 — bisher war
 * alles davon Freitext, obwohl es im Kontakt schon steht.
 *
 * Nur auf dem angemeldeten Weg (`contactLookup`): die Endpunkte sind
 * RBAC-geschützt, der Kunde über den öffentlichen Link kennt den
 * Kontaktbestand nicht.
 */

export type KontaktAnschrift = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type KontaktKonto = {
  id: number;
  iban: string;
  bankName: string;
  /** Lesbare Zeile für die Auswahl: „Sparkasse KölnBonn · DE12 … 3456". */
  label: string;
  isDefault: boolean;
};

export type KontaktPerson = { id: number; label: string };

type RohAdresse = {
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  isDefault?: boolean | null;
};

type RohKonto = {
  id?: number;
  iban?: string | null;
  bankName?: string | null;
  bankDescription?: string | null;
  accountHolder?: string | null;
  isDefault?: boolean | null;
};

type RohPerson = {
  id?: number;
  displayName?: string | null;
  firstName?: string | null;
  lastname?: string | null;
};

const t = (v: string | null | undefined) => (v ?? "").trim();

/** Standardanschrift, sonst die erste mit Inhalt. */
export function standardAnschrift(adressen: RohAdresse[] | null | undefined): KontaktAnschrift | null {
  const liste = (adressen ?? []).filter((a) => t(a.street) || t(a.city) || t(a.postalCode));
  const a = liste.find((x) => x.isDefault) ?? liste[0];
  if (!a) return null;
  return { street: t(a.street), postalCode: t(a.postalCode), city: t(a.city), country: t(a.country) };
}

/** IBAN in Viererblöcken, Mitte gekürzt — reicht zum Wiedererkennen. */
const ibanKurz = (iban: string) => {
  const roh = iban.replace(/\s+/g, "");
  return roh.length > 10 ? `${roh.slice(0, 4)} … ${roh.slice(-4)}` : roh;
};

export function kontenAusKontakt(konten: RohKonto[] | null | undefined): KontaktKonto[] {
  return (konten ?? [])
    .filter((k) => typeof k.id === "number" && t(k.iban))
    .map((k) => {
      const bank = t(k.bankName) || t(k.bankDescription);
      return {
        id: k.id as number,
        iban: t(k.iban),
        bankName: bank,
        label: [bank || "Konto", ibanKurz(t(k.iban)), t(k.accountHolder) || null]
          .filter(Boolean)
          .join(" · "),
        isDefault: !!k.isDefault,
      };
    })
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

export function personenAusMitarbeitern(rows: RohPerson[] | null | undefined): KontaktPerson[] {
  return (rows ?? [])
    .filter((r) => typeof r.id === "number")
    .map((r) => ({
      id: r.id as number,
      label: t(r.displayName) || [t(r.firstName), t(r.lastname)].filter(Boolean).join(" ") || `Kontakt ${r.id}`,
    }));
}

export async function ladeKontaktStammdaten(
  contactId: number,
): Promise<{ anschrift: KontaktAnschrift | null; konten: KontaktKonto[] }> {
  const { data } = await apiFetch<{ addresses?: RohAdresse[]; bankAccounts?: RohKonto[] }>(
    `contact/${contactId}`,
  );
  return { anschrift: standardAnschrift(data?.addresses), konten: kontenAusKontakt(data?.bankAccounts) };
}

export async function ladeMitarbeiter(contactId: number): Promise<KontaktPerson[]> {
  const { data } = await apiFetch<RohPerson[]>(`contact-relation/mitarbeiter/${contactId}`);
  return personenAusMitarbeitern(data);
}
